import type { CSSProperties } from 'react'
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'

import { useAppSelector } from 'hooks/reduxHooks'
import {
  elapsedMs,
  formatElapsed,
  GHA_STEPS,
  getGhaStepsMask,
  isGhaVerifier,
} from 'lib/metasearch/ghaLoadingDiagnostics'

const PANEL_STYLE: CSSProperties = {
  position: 'fixed',
  top: 8,
  left: 8,
  zIndex: 2147483647,
  padding: '8px 12px',
  background: 'rgba(0, 0, 0, 0.85)',
  color: '#fff',
  font: '12px/1.6 ui-monospace, monospace',
  borderRadius: 4,
  pointerEvents: 'none',
  whiteSpace: 'pre',
}

const CLOCK_TICK_MS = 50
const CLOCK_PLACEHOLDER = '0:00.00'
const ELAPSED_LABEL = 'elapsed '

const subscribeNoop = () => () => {}

let ghaDiagnosticsInstanceId = 0

/** Bot only overlay for GHA price-validation screenshots. Renders nothing for customers. */
function GhaLoadingDiagnostics() {
  const hydrated = useSyncExternalStore(subscribeNoop, () => true, () => false)
  const [startMs] = useState(() => {
    ghaDiagnosticsInstanceId += 1
    return ghaDiagnosticsInstanceId === 1 ? 0 : elapsedMs()
  })
  const [doneAt, setDoneAt] = useState<Record<string, number>>({})
  const pathname = useAppSelector(state => state.router.location.pathname)
  const mask = useAppSelector(getGhaStepsMask)
  const clockRef = useRef<HTMLSpanElement>(null)

  const enabled = hydrated && isGhaVerifier(pathname)
  const allDone = mask.length > 0 && !mask.includes('0')

  // Direct DOM updates so the clock keeps ticking during heavy renders.
  useEffect(() => {
    if (!enabled || allDone) return

    const intervalId = window.setInterval(() => {
      if (clockRef.current) clockRef.current.textContent = formatElapsed(elapsedMs() - startMs)
    }, CLOCK_TICK_MS)

    return () => window.clearInterval(intervalId)
  }, [enabled, allDone, startMs])

  useEffect(() => {
    if (!enabled) return

    setDoneAt(previous => {
      const newlyDone = GHA_STEPS.filter((step, index) => mask[index] === '1' && previous[step.id] === undefined)
      if (!newlyDone.length) return previous

      const at = elapsedMs()
      return {
        ...previous,
        ...Object.fromEntries(newlyDone.map(step => [step.id, at])),
      }
    })
  }, [enabled, mask])

  if (!enabled || allDone) return null

  return (
    <div style={PANEL_STYLE} data-testid="gha-loading-diagnostics">
      <div>
        {ELAPSED_LABEL}
        <span ref={clockRef}>{CLOCK_PLACEHOLDER}</span>
      </div>
      {GHA_STEPS.map((step, index) => {
        const tick = mask[index] === '1' ? '[x]' : '[ ]'
        const timing = doneAt[step.id] === undefined ? '' : formatElapsed(doneAt[step.id] - startMs)
        const row = `${tick} ${step.label.padEnd(17)}${timing}`

        return <div key={step.id}>{row}</div>
      })}
    </div>
  )
}

export default GhaLoadingDiagnostics
